import { useQuery } from '@tanstack/react-query';
import { Building2, Users, UserCircle, ChevronRight } from 'lucide-react';
import { Button, Card } from '@employer-on-record/ui';
import { Link } from 'react-router-dom';
import { dashboardApi, companiesApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PageHead } from '@/components/PageHead';
import { StatCard } from '@/components/StatCard';
import { formatDateShort, formatNumber } from '@/lib/utils';

export default function DashboardPage() {
	const { user } = useAuthStore();
	const { data: stats } = useQuery({ queryKey: ['admin-overview'], queryFn: () => dashboardApi.admin() });
	const { data: companiesResp } = useQuery({ queryKey: ['admin-companies'], queryFn: () => companiesApi.list() });

	const s = stats?.data;
	const recent = (companiesResp?.data ?? []).slice(0, 6);

	return (
		<>
			<PageHead title="Overview" subtitle={`Signed in as ${user?.name ?? 'Canis Major staff'}`} />

			<div className="grid grid-cols-3 gap-4 mb-6">
				<StatCard label="Companies" value={s?.companyCount ?? 0} icon={Building2} tone="blue" />
				<StatCard label="HR users" value={s?.hrCount ?? 0} icon={Users} tone="violet" />
				<StatCard label="Active employees" value={s?.employeeCount ?? 0} icon={UserCircle} tone="green" />
			</div>

			<Card>
				<div className="flex items-center px-5 py-4 border-b">
					<h3 className="text-sm font-semibold">Recent companies</h3>
					<Button asChild variant="ghost" size="sm" className="ml-auto">
						<Link to="/companies">All companies <ChevronRight className="h-3 w-3" /></Link>
					</Button>
				</div>
				{recent.length === 0 ? (
					<div className="text-center text-muted-foreground p-10 text-sm">No companies yet — create your first client to get started.</div>
				) : (
					<table className="w-full text-[13px]">
						<thead>
							<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="text-left px-4 py-2.5 font-semibold">Name</th>
								<th className="text-left px-4 py-2.5 font-semibold">Industry</th>
								<th className="text-left px-4 py-2.5 font-semibold">HR</th>
								<th className="text-left px-4 py-2.5 font-semibold">Employees</th>
								<th className="text-left px-4 py-2.5 font-semibold">Approval levels</th>
								<th className="text-left px-4 py-2.5 font-semibold">Created</th>
							</tr>
						</thead>
						<tbody>
							{recent.map((c) => (
								<tr key={c.id} className="border-b last:border-0 hover:bg-background/60">
									<td className="px-4 py-3.5">
										<Link to={`/companies/${c.id}`} className="font-medium hover:underline">{c.name}</Link>
									</td>
									<td className="px-4 py-3.5 text-muted-foreground">{c.industry || '—'}</td>
									<td className="px-4 py-3.5 tabular">{formatNumber(c.hrCount)}</td>
									<td className="px-4 py-3.5 tabular">{formatNumber(c.employeeCount)}</td>
									<td className="px-4 py-3.5 text-muted-foreground">Leave {c.leaveApprovalLevels} · Reimburse {c.reimbursementApprovalLevels}</td>
									<td className="px-4 py-3.5 text-muted-foreground">{formatDateShort(c.createdAt)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Card>
		</>
	);
}
